// What the Import files button sends, through the mutations the web already uses to create
// each thing:
//   - Recording        → CreateRecording (as the old Recordings import did), every recorded
//                        track and segment, with `segmentStartIndices` at each GPS gap
//   - Planned hike     → CreateHike (as the old My Hikes import did), one planned trail per
//                        day, the file's waypoints as `hikeHighlightsAttributes`. A hike "from
//                        waypoints" is one day drawn straight through them, in file order.
//   - Waypoints        → CreateWaypoints then CreateWaypointGroup (as the old Waypoints import did)
// The payload builders are pure; `saveImports` takes its GraphQL client and elevation lookup
// as arguments so its sequencing is unit-tested with fakes (importSave.test.ts).

import { buildImportRecordingInput } from '../../apps/import_recording/importRecordingInput';
import {
  buildCreateHikeInput,
  calculateDistances,
  dayTitle,
  processTrackData,
  simplifyPoints,
  stripEmoji,
  type ImportDay as HikeDay
} from '../helpers/gpxHikeImport';
import { titleFromFileName } from '../helpers/titleFromFileName';
import { haversineM, numberRange } from './importRules';
import type { ImportPoint, ImportPreview, ImportWaypoint } from './importTypes';
import { fromWaypointsDay, plannedDays, recordingSegments, waypointsToAdd, type ImportDay } from './importPayloads';
import {
  choiceDestination,
  type ImportCounts,
  type ImportDestination,
  type ImportFileEntry,
  type ImportFileSettings
} from './importScreenState';

/** The title a file's import is saved under: its name without the extension. */
export function importTitle(fileName: string, fallback: string): string {
  return stripEmoji(titleFromFileName(fileName)) || fallback;
}

// ---- recordings ---------------------------------------------------------------------------

/**
 * The CreateRecording input for a file: every recorded track, in time order, keeping the
 * file's own elevation (0 where it had none) and times. Distance runs on within a segment and
 * doesn't count the GPS gap between two; each later segment's first point is a
 * `segmentStartIndices` entry, the way a continued recording is stored.
 */
export function recordingInput(p: ImportPreview, title: string, clientId: string, nowIso: string) {
  const segments = recordingSegments(p).filter(seg => seg.length);
  const coords: string[] = [];
  const altitudes: number[] = [];
  const timeStamps: number[] = [];
  const segmentStartIndices: number[] = [];
  let distance = 0;
  segments.forEach((seg, si) => {
    if (si > 0) segmentStartIndices.push(coords.length);
    seg.forEach((pt, i) => {
      if (i > 0) distance += haversineM(seg[i - 1], pt);
      const ele = pt.ele ?? 0;
      coords.push(`${pt.lon} ${pt.lat} ${ele} ${Math.round(distance * 10) / 10}`);
      altitudes.push(ele);
      if (pt.time != null) timeStamps.push(pt.time);
    });
  });
  const allTimed = timeStamps.length === coords.length;
  const range = numberRange(timeStamps);
  const first = range ? range.min : null;
  const last = range ? range.max : null;
  const input = buildImportRecordingInput(
    {
      title,
      lineString: `LINESTRING (${coords.join(', ')})`,
      // Per-point times are positional on the server, so they go only when every point has one.
      timeStamps: allTimed ? timeStamps : [],
      altitudes,
      elapsedSeconds: first != null && last != null ? Math.floor((last - first) / 1000) : 0,
      createdAt: first != null ? new Date(first).toISOString() : undefined
    },
    clientId,
    nowIso
  );
  return segmentStartIndices.length ? { ...input, segmentStartIndices } : input;
}

// ---- planned hikes ------------------------------------------------------------------------

/** A file's elevation is used when at least 90% of its points carry one (spec "Elevation"). */
export function hasFileElevation(points: ImportPoint[]): boolean {
  if (!points.length) return false;
  return points.filter(p => p.ele != null).length / points.length >= 0.9;
}

/**
 * The days a planned-hike choice saves: a hike from waypoints is one day straight through the
 * kept waypoints in file order; every other one as drawn.
 */
export function daysFor(entry: ImportFileEntry): ImportDay[] {
  const p = entry.preview as ImportPreview;
  return entry.settings.choice === 'fromWaypoints' ? [fromWaypointsDay(p)] : plannedDays(p, entry.settings);
}

/** One day worked up for CreateHike: simplified, distances along it, titled. */
export function hikeDay(day: ImportDay, hikeTitle: string, index: number, total: number): HikeDay {
  const points = simplifyPoints(day.points.map(p => ({ lat: p.lat, lon: p.lon, elevation: p.ele ?? 0 })));
  return processTrackData(points, dayTitle(hikeTitle, { name: day.name ?? undefined, points }, index, total));
}

/** The highlights a planned hike gets from a file's waypoints, as the planner's own import makes them. */
export function highlightAttributes(waypoints: ImportWaypoint[], unnamed: (n: number) => string) {
  return waypoints.map((w, i) => ({
    type: 'other',
    title: stripEmoji(w.name.trim()) || unnamed(i + 1),
    lonlat: `POINT(${w.point.lon} ${w.point.lat})`
  }));
}

export function hikeInput(
  { hikeTitle, fileName, days, waypoints, unnamed }:
  { hikeTitle: string; fileName: string; days: HikeDay[]; waypoints: ImportWaypoint[]; unnamed: (n: number) => string }
) {
  const input = buildCreateHikeInput({ hikeTitle, fileName, days });
  return waypoints.length ? { ...input, hikeHighlightsAttributes: highlightAttributes(waypoints, unnamed) } : input;
}

// ---- waypoint groups ----------------------------------------------------------------------

/** The CreateWaypoints input, shaped as the Waypoints dialog's own import sends it. */
export function waypointsInput(waypoints: ImportWaypoint[], unnamed: string) {
  return waypoints.map(w => ({
    type: 'other',
    title: w.name.trim() || unnamed,
    description: '',
    public: true,
    coordinateReferenceSystem: 'EPSG:4326',
    lonlat: `POINT (${w.point.lon} ${w.point.lat})`
  }));
}

// ---- saving -------------------------------------------------------------------------------

/** What one saved file created, for the result toast and the host page. */
export interface ImportCreated {
  destination: ImportDestination;
  id: string;
  title: string;
  url?: string | null;
}

export interface ImportFailure {
  /** The entry's id, so the failed file can be sent in (importFailureReport.ts). */
  id: string;
  name: string;
  message: string;
}

export interface ImportSaveDeps {
  /** graph_client.request */
  request: (document: unknown, variables: Record<string, unknown>) => Promise<any>;
  /** [lat, lng] in, elevations out — getElevationData; null when the service is unavailable. */
  elevation: (coords: number[][]) => Promise<Array<{ lat: number; lng: number; elevation: number }> | null>;
  mutations: { createRecording: unknown; createHike: unknown; createWaypoints: unknown; createWaypointGroup: unknown };
  /** Localised fallbacks. */
  texts: { defaultTitle: string; unnamedWaypoint: string; unnamedHighlight: (n: number) => string };
  now?: () => Date;
}

const errorsOf = (payload: any): string | null => {
  const errors = payload?.errors;
  if (!Array.isArray(errors) || !errors.length) return null;
  return errors.map((e: any) => (e.errors ? `${e.fieldName}: ${e.errors.join(', ')}` : String(e.message ?? e))).join('\n');
};

/** Replaces a day's elevations with the elevation service's, when the file had too few. */
async function withElevation(day: HikeDay, raw: ImportPoint[], deps: ImportSaveDeps): Promise<HikeDay> {
  if (hasFileElevation(raw)) return day;
  const data = await deps.elevation(day.coordinates.map(c => [c[1], c[0]]));
  if (!data || data.length !== day.coordinates.length) return day;
  const coordinates = data.map(e => [e.lng, e.lat, e.elevation]);
  return { ...day, coordinates, altitudes: data.map(e => e.elevation), distances: calculateDistances(coordinates) };
}

export async function saveEntry(entry: ImportFileEntry, deps: ImportSaveDeps): Promise<ImportCreated> {
  const p = entry.preview as ImportPreview;
  const settings: ImportFileSettings = entry.settings;
  const title = importTitle(entry.name, deps.texts.defaultTitle);
  const now = (deps.now ?? (() => new Date()))();
  switch (settings.choice) {
    case 'recording': {
      const input = recordingInput(p, title, `upload-${now.getTime()}-${Math.floor(Math.random() * 1e6)}`, now.toISOString());
      const res = await deps.request(deps.mutations.createRecording, { input });
      const created = res?.createRecording;
      const error = errorsOf(created);
      if (error || !created?.id) throw new Error(error ?? 'createRecording returned no id');
      return { destination: 'recordings', id: String(created.id), title: created.title ?? title, url: created.shareableUrl ?? null };
    }
    case 'planned':
    case 'fromWaypoints': {
      const days = daysFor(entry);
      const worked: HikeDay[] = [];
      for (let i = 0; i < days.length; i++) {
        worked.push(await withElevation(hikeDay(days[i], title, i, days.length), days[i].points, deps));
      }
      const input = hikeInput({
        hikeTitle: title,
        fileName: entry.name,
        days: worked,
        waypoints: waypointsToAdd(p, settings),
        unnamed: deps.texts.unnamedHighlight
      });
      const res = await deps.request(deps.mutations.createHike, { input });
      const error = errorsOf(res?.createHike);
      const hike = res?.createHike?.hike;
      if (error || !hike?.id) throw new Error(error ?? 'createHike returned no hike');
      return { destination: 'plannedHikes', id: String(hike.id), title: hike.name ?? title };
    }
    case 'waypoints': {
      const res = await deps.request(deps.mutations.createWaypoints, {
        input: { input: waypointsInput(waypointsToAdd(p, settings), deps.texts.unnamedWaypoint) }
      });
      const ids = (res?.createWaypoints ?? []).map((w: any) => w?.id).filter(Boolean);
      if (!ids.length) throw new Error('createWaypoints returned no waypoints');
      const group = await deps.request(deps.mutations.createWaypointGroup, { waypointGroup: { title, waypointIds: ids } });
      const error = errorsOf(group?.createWaypointGroup);
      if (error || !group?.createWaypointGroup?.id) throw new Error(error ?? 'createWaypointGroup returned no group');
      return { destination: 'waypoints', id: String(group.createWaypointGroup.id), title };
    }
    default:
      throw new Error(`${settings.choice} is not saved here`);
  }
}

/** What a save created and what failed — the result summary's content. */
export interface ImportSavedResult {
  created: ImportCreated[];
  failures: ImportFailure[];
  counts: ImportCounts;
  notImported: number;
}

/** One file at a time, each on its own so one bad file doesn't stop the rest. */
export async function saveImports(
  entries: ImportFileEntry[],
  deps: ImportSaveDeps
): Promise<{ created: ImportCreated[]; failures: ImportFailure[] }> {
  const created: ImportCreated[] = [];
  const failures: ImportFailure[] = [];
  for (const entry of entries) {
    if (!choiceDestination(entry.settings.choice)) continue;
    try {
      created.push(await saveEntry(entry, deps));
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      failures.push({ id: entry.id, name: entry.name, message });
    }
  }
  return { created, failures };
}
